/**
 * Division too big to lay out as blocks — 2443 ÷ 17 — done the way it is
 * actually taught: chunking, a.k.a. partial quotients. You keep taking away
 * batches of 17 (a hundred of them, ten of them, one of them) until fewer than
 * 17 are left. The batches you took are the answer; what is left is the
 * remainder.
 *
 * Pure logic, no canvas, so the maths can be tested exhaustively.
 */

export interface Chunk {
  /** How many divisors were taken in one go: 1, 2, 5, 10, 100... */
  multiple: number;
  /** multiple * divisor — what left the pile. */
  product: number;
}

export interface DivideState {
  dividend: number;
  divisor: number;
  chunks: Chunk[];
}

/**
 * The batch sizes offered as buttons, biggest first.
 *
 * They scale with the sum rather than being a fixed list. A fixed biggest
 * batch of a hundred is fine for 2443 ÷ 17 and hopeless for 999999 ÷ 7, which
 * would need fourteen hundred of them — the child would still be tapping at
 * bedtime. Going up in powers of ten instead keeps the number of batches down
 * to the sum of the digits of the answer, which is what chunking is for.
 */
export function chunkSizes(state: DivideState): number[] {
  if (state.divisor <= 0) return [1];
  const sizes: number[] = [];
  let power = 1;
  while (power * 10 * state.divisor <= state.dividend) power *= 10;
  for (let p = power; p >= 10; p /= 10) sizes.push(p);
  sizes.push(5, 2, 1);
  return sizes;
}

export function createDivide(dividend: number, divisor: number): DivideState {
  return { dividend, divisor, chunks: [] };
}

export function quotientSoFar(state: DivideState): number {
  return state.chunks.reduce((sum, c) => sum + c.multiple, 0);
}

export function remaining(state: DivideState): number {
  return state.dividend - state.chunks.reduce((sum, c) => sum + c.product, 0);
}

/** A batch can be taken only while it still fits in what is left. */
export function canTake(state: DivideState, multiple: number): boolean {
  if (state.divisor <= 0) return false;
  return multiple * state.divisor <= remaining(state);
}

export function take(state: DivideState, multiple: number): DivideState {
  if (!canTake(state, multiple)) return state;
  return { ...state, chunks: [...state.chunks, { multiple, product: multiple * state.divisor }] };
}

export function undo(state: DivideState): DivideState {
  return { ...state, chunks: state.chunks.slice(0, -1) };
}

/** Finished when fewer than one divisor is left over. */
export function isDone(state: DivideState): boolean {
  return state.divisor > 0 && remaining(state) < state.divisor;
}

/** The largest offered batch that still fits — powers the nudge. */
export function biggestChunk(state: DivideState): number | null {
  for (const size of chunkSizes(state)) {
    if (canTake(state, size)) return size;
  }
  return null;
}

/** The answer, once the chunks are all taken. */
export function answer(state: DivideState): { quotient: number; remainder: number } {
  return { quotient: quotientSoFar(state), remainder: remaining(state) };
}

/**
 * One short line of guidance: what the next sensible move is, or what the
 * finished sum says. Kept plain — no jargon, no dashes.
 */
export function divideHint(state: DivideState): string {
  const { divisor } = state;
  if (divisor <= 0) return 'Pick a number to share by';
  const left = remaining(state);

  if (isDone(state)) {
    const { quotient, remainder } = answer(state);
    if (remainder === 0) return `${divisor} goes into ${state.dividend} exactly ${quotient} times`;
    return `${quotient} lots of ${divisor} is ${quotient * divisor}, and ${remainder} is left over`;
  }

  const next = biggestChunk(state);
  if (next === null) return `Take away ${divisor}`;
  if (state.chunks.length === 0) return `${next} lots of ${divisor} is ${next * divisor}. That fits in ${left}.`;
  return `${left} left. ${next} more lots of ${divisor} fits.`;
}

/** The check a teacher asks for: multiply back and add the remainder. */
export function checkLine(state: DivideState): string {
  const { quotient, remainder } = answer(state);
  const product = quotient * state.divisor;
  if (remainder === 0) return `${state.divisor} x ${quotient} = ${state.dividend}`;
  return `${state.divisor} x ${quotient} = ${product}, and ${product} + ${remainder} = ${state.dividend}`;
}

/** Take the biggest batch repeatedly — used by the "show me" button. */
export function solve(state: DivideState): DivideState {
  let current = state;
  // Bounded so a bad divisor can never spin forever.
  for (let i = 0; i < 500 && !isDone(current); i++) {
    const next = biggestChunk(current);
    if (next === null) break;
    current = take(current, next);
  }
  return current;
}
